import { mat4, vec2 } from 'gl-matrix';
import { gl } from '../../../Engine';
import { StructureSetObject } from '../../../data/structure/StructureSetObject';
import { TextureView } from '../../../view/TextureView';
import { PlaneView } from '../../../view/views/PlaneView';
import { ShaderProgram } from '../../utils/ShaderProgram';
import { UniformMatrix } from '../../utils/uniforms/UniformMatrix';
import { UniformVec2 } from '../../utils/uniforms/UniformVec2';
import { UniformVec4 } from '../../utils/uniforms/UniformVec4';
import { Renderer } from '../Renderer';

export class ContourRenderer implements Renderer {
    /**
     * Multipliers for calculating the thickness of contour lines in the shader.\
     * This is used in computations so that the a single pixel width line is achieved with a multiplier of '1.0 / 2.0'.
     */
    private static readonly halfLineWidth: vec2 = vec2.fromValues(
        1.5 / 2.0,
        1.5 / 2.0
    );

    private static readonly halfLineWidthBold: vec2 = vec2.fromValues(
        2.5 / 2.0,
        2.5 / 2.0
    );

    /**
     * Readonly {@link ShaderProgram} of type {@link ContourTextureShader}, which is used to {@link StructureSetObject}s.\
     * For {@link PlaneView}s it provides a simple contour rendering, which renders only the outlines of contours.
     */
    private readonly textureShader: ContourTextureShader;

    public constructor() {
        this.textureShader = new ContourTextureShader();
    }

    /**
     * Method to render {@link StructureSetObject StructureSets} on a {@link TextureView}.
     * @throws An {@link Error}, if there's no implementation for {@link StructureSetObject StructureSet} rendering for the type of {@link view}.
     * @param view {@link TextureView} on which the {@link structureSets} are being rendered.\
     * This parameter is needed to define the viewport.
     * @param structureSets {@link Array} of {@link StructureSetObject}s that are rendered on the {@link TextureView}.
     * @returns True to indicate that the {@link structureSets} were successfully rendered.
     */
    public render(
        view: TextureView,
        structureSets: StructureSetObject[]
    ): boolean {
        if (view instanceof PlaneView) {
            gl.enable(gl.BLEND);
            gl.blendEquation(gl.FUNC_ADD);
            gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
            this.textureShader.start();

            const viewToPatient = mat4.invert(
                mat4.create(),
                view.projectionViewMatrix
            );
            //remove translation
            viewToPatient[12] = viewToPatient[13] = viewToPatient[14] = 0.0;

            for (const structureSet of structureSets) {
                for (const roi of structureSet.structures) {
                    if (
                        !roi.isVisible &&
                        roi !== structureSet.selectedStructure
                    ) {
                        continue;
                    }
                    //TODO: If texture is not defined, could also check if sequence bb & plane intersect.
                    const texture = roi.texture;
                    if (!texture) {
                        continue;
                    }

                    const contourRenderWidth = vec2.divide(
                        vec2.create(),
                        ContourRenderer.halfLineWidth,
                        view.size
                    );
                    const contourRenderWidthBold = vec2.divide(
                        vec2.create(),
                        ContourRenderer.halfLineWidthBold,
                        view.size
                    );

                    const contourThickness = roi.contourThickness;
                    vec2.multiply(contourRenderWidth, contourRenderWidth, [
                        contourThickness,
                        contourThickness,
                    ]);
                    vec2.multiply(
                        contourRenderWidthBold,
                        contourRenderWidthBold,
                        [contourThickness, contourThickness]
                    );
                    if (roi === structureSet.selectedStructure) {
                        this.textureShader.relativeDist.loadVec2(
                            contourRenderWidthBold
                        );
                    } else {
                        this.textureShader.relativeDist.loadVec2(
                            contourRenderWidth
                        );
                    }

                    const vao = texture.crossSection(
                        view.projectionViewMatrix,
                        structureSet.transformationMatrix
                    );
                    if (vao) {
                        this.textureShader.colorMask.loadVec4(roi.color, 1);
                        const patientToImage = mat4.clone(
                            texture.patientToImage
                        );
                        patientToImage[12] =
                            patientToImage[13] =
                            patientToImage[14] =
                                0;
                        this.textureShader.viewToTexture.loadMatrix(
                            mat4.multiply(
                                mat4.create(),
                                patientToImage,
                                viewToPatient
                            )
                        );
                        texture.bindTexture();

                        vao.bind(0, 1);
                        gl.drawElements(
                            gl.TRIANGLES,
                            vao.indexCount,
                            gl.UNSIGNED_INT,
                            0
                        );
                        vao.unbind(0, 1);
                        vao.delete();
                        texture.unbindTexture();
                    }
                }
            }
            this.textureShader.stop();
            gl.disable(gl.BLEND);
            return true;
        } else {
            throw new Error(
                `ContourRenderer on View of type ${view.constructor} currently not supported.
            `
            );
        }
    }

    /**
     * Method to delete the {@link ContourTextureShader} allocated by this {@link ContourRenderer}.\
     * This method should always be called when the {@link ContourRenderer} is no longer needed to de-allocate the WebGL memory.
     * @returns True to indicate that the {@link ContourTextureShader} was successfully deleted.
     */
    delete(): boolean {
        this.textureShader.delete();
        return true;
    }
}

/**
 * {@link ShaderProgram} that renders a 2D cross-section of {@link ContourTexture} and a plane.
 */
class ContourTextureShader extends ShaderProgram {
    private static readonly VERTEX_CONTENT = `#version 300 es
    
    precision highp float;
    
    in vec2 position;
    in vec3 textureCoords;
    
    out vec3 pass_textureCoordinates;
    
    void main(){

        gl_Position = vec4(position, 0.0, 1.0);

        pass_textureCoordinates = textureCoords;
    }
    `;

    private static readonly FRAGMENT_CONTENT = `#version 300 es
    
    precision highp float;
    precision highp sampler3D;
    
    in vec3 pass_textureCoordinates;
    
    out vec4 outColor;
    
    uniform vec4 colorMask;
    uniform sampler3D sampler;
    
    const float diagonal = 0.70710678118;
    uniform mat4 viewToTexture;
    uniform vec2 relativeDist;
    
    bool isWithin(float x, float y) {
        vec4 rotated = viewToTexture * vec4(x * relativeDist.x, y * relativeDist.y, 0.0, 1.0);
        vec3 dest = pass_textureCoordinates + rotated.xyz;
        if(dest.x <= 0.0 || dest.x >= 1.0 || dest.y <= 0.0 || dest.y >= 1.0 || dest.z <= 0.0 || dest.z >= 1.0){
            return false;
        }
        return texture(sampler, dest).r >= 0.5;
    }
    
    void main(){
        float sdfValue = texture(sampler, pass_textureCoordinates).r;
        bool within = sdfValue >= 0.5;
        if(
        isWithin(-diagonal, -diagonal) != within ||
        isWithin(0.0, -1.0) != within ||
        isWithin(diagonal, -diagonal) != within ||
        isWithin(-1.0, 0.0) != within ||
        isWithin(1.0, 0.0) != within ||
        isWithin(-diagonal, diagonal) != within ||
        isWithin(0.0, 1.0) != within ||
        isWithin(diagonal, diagonal) != within){
            outColor = colorMask;
        } else {
            //outColor = colorMask;
            //outColor.a = sdfValue;
            discard;
        }
    }`;

    /**
     * {@link UniformVec4} that takes the line color as an input and provides it to {@link ContourTextureShader}.\
     * That color value is used as is to color the contour outlines.
     */
    public readonly colorMask: UniformVec4;

    /**
     * {@link UniformMatrix} that provides a {@link ReadonlyMat4 Matrix} to convert distances from ViewSpace to TextureSpace.\
     * This is used to convert the {@link ContourRenderer.lineWidth lineWidth} into texture units.
     */
    public readonly viewToTexture: UniformMatrix;

    /**
     * {@link UniformVec2} that provides a {@link ReadonlyVec2 Vector} which represents the {@link ContourRenderer.lineWidth lineWidth} in ViewSpace.
     */
    public readonly relativeDist: UniformVec2;

    constructor() {
        super(
            ContourTextureShader.VERTEX_CONTENT,
            ContourTextureShader.FRAGMENT_CONTENT,
            'position',
            'textureCoords'
        );
        this.colorMask = new UniformVec4('colorMask');
        this.viewToTexture = new UniformMatrix('viewToTexture');
        this.relativeDist = new UniformVec2('relativeDist');
        this.storeUniformLocations(
            this.colorMask,
            this.viewToTexture,
            this.relativeDist
        );
    }
}
